import { Button, Divider, Input, Modal, Popconfirm, Select, Table } from "antd";
import { useCallback, useEffect, useState } from "react";
import "./DiningRDIntegration.scss";
import DRDApi from "../../../api/DRDApi";
import { DiningRDCommunityDto, LocationDto } from "../../../generated/dashboard-client";
import { locationsState, memberState } from "../../../state/atoms";
import { useRecoilValue } from "recoil";
import Column from "antd/lib/table/Column";
import { PlusOutlined } from "@ant-design/icons";
import { handleServerError } from "../../../util/ErrorHandler";

interface SelectOption {
  label: string | undefined;
  value: number | undefined;
}

const mapAsSelectOption = (location: LocationDto | undefined) => {
  if (!location) {
    return null;
  }
  return {
    label: location.name,
    value: location.name
  } as SelectOption;
};

export default function DiningRDIntegration() {
  const member = useRecoilValue(memberState);
  const [communities, setCommunities] = useState<DiningRDCommunityDto[]>();
  const locations = useRecoilValue(locationsState);
  const [openSelectId, setOpenSelectId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [communityId, setCommunityId] = useState<number | undefined>(undefined);
  const [editCommunityId, setEditCommunityId] = useState<number | undefined>(undefined);
  const [communityName, setCommunityName] = useState<string>("");
  const [locationName, setLocationName] = useState<string>('');

  const [unmappedLocations, setUnmappedLocations] = useState<LocationDto[]>([]);

  const fetchCommunities = useCallback(() => {
    DRDApi.getCommunities().then(resp => {
      const comms = resp.data;
      setCommunities(comms.sort((a, b) => (a.id || 0) - (b.id || 0)));
    }).catch(err => {
      setCommunities([]);
      handleServerError(err);
    });
  }, []);

  useEffect(fetchCommunities, [fetchCommunities]);

  useEffect(() => {
    if (!locations || !communities) {
      setUnmappedLocations([]);
      return;
    }
    const mappedLocations = communities.map(it => it.locationId);
    const unmappedLocations = locations.filter(it => !mappedLocations.find(loc => loc === it.internalId));
    setUnmappedLocations(unmappedLocations);
  }, [locations, communities, setUnmappedLocations]);

  function deleteDRD(community: DiningRDCommunityDto) {
    if (!community.id) return;
    DRDApi.deleteCommunity(community.id).then(() => {
      setCommunities(old => old?.filter(it => it.id !== community.id));
    });
  }

  function onCreateNewLocationClick() {
    setOpenSelectId(null);
    window.MHM.ProModal.addLocation({ memberId: member.id, onSuccess: () => window.location.reload() });
  }

  function onSave() {
    setLoading(true);
    const dto: DiningRDCommunityDto = {
      memberId: member.ownerId || member.internalId,
      id: communityId,
      name: communityName,
      locationId: locations.find(it => it.name === locationName)?.internalId
    };

    if (editCommunityId && editCommunityId !== communityId) {
      DRDApi.deleteCommunity(editCommunityId);
    }

    let call;
    if (communities?.find(it => it.id === communityId)) {
      call = DRDApi.updateCommunity(dto);
    } else {
      call = DRDApi.createCommunity(dto);
    }

    call.then(() => {
      setLoading(false);
      closeModal();
      fetchCommunities();
    }).catch(handleServerError);
  }

  function closeModal() {
    setModalOpen(false);
  }

  function onAddClick() {
    setCommunityId(undefined);
    setCommunityName("");
    setLocationName('');
    setModalOpen(true);
  }

  function onEditClick(community: DiningRDCommunityDto) {
    setEditCommunityId(community.id);
    setCommunityId(community.id);
    setCommunityName(community.name || "");
    setLocationName(locations.find(it => it.internalId === community.locationId)?.name || '');
    setModalOpen(true);
  }

  return <div className={"drd"}>
    <h2>DiningRD Integration</h2>
    <Button type={"primary"} onClick={onAddClick}>Add new community</Button>
    <Table dataSource={communities} pagination={false} loading={!communities}
           locale={{ emptyText: "No communities added yet" }}>
      <Column title={"DiningRD Community Name"} dataIndex="name" key="name"
              sorter={(a: DiningRDCommunityDto, b: DiningRDCommunityDto) => a.name?.localeCompare(b.name ?? "") || 0} />

      <Column title="DiningRD Community ID" dataIndex="id" key="id"
              sorter={(a: DiningRDCommunityDto, b: DiningRDCommunityDto) => (a?.id || 0) - (b?.id || 0)} />

      <Column title="Corp ID" dataIndex="corporation" key="corporation"
              sorter={(a: DiningRDCommunityDto, b: DiningRDCommunityDto) => (a?.corporation?.id || 0) - (b?.corporation?.id || 0)}
              render={(_: string, record: DiningRDCommunityDto) => (record.corporation?.id || "")} />

      <Column title="Mapped MHM Location" dataIndex="locationId" key="locationId"
              sorter={(a: DiningRDCommunityDto, b: DiningRDCommunityDto) => (a?.locationId || 0) - (b?.locationId || 0)}
              render={(_: string, record: DiningRDCommunityDto) => <span>{locations?.find(it => it.internalId === record.locationId)?.name}</span>} />

      <Column title="Options" dataIndex="options" key="options"
              render={(_: string, record: DiningRDCommunityDto) => <div className={"options"}>
                <Button onClick={() => onEditClick(record)}>Edit</Button>
                <Popconfirm title={`Are you sure?`}
                            placement="bottom"
                            okText="Yes"
                            onConfirm={() => deleteDRD(record)}><Button>Delete</Button></Popconfirm>
              </div>} />
    </Table>

    <Modal className={"clean one drd-modal"} open={modalOpen} onCancel={closeModal}>
      <section>
        <div>
          <label>CommunityID</label>
          <Input placeholder={"CommunityID"} size="large" type={"text"} value={communityId}
                 onChange={(ev) => setCommunityId(parseInt(ev.target.value.replace(/[^0-9]/g, '') || '0'))} />
        </div>
        <div>
          <label>Community Name</label>
          <Input placeholder={"Community Name"} size="large" type={"text"} value={communityName}
                 onChange={(ev) => setCommunityName(ev.target.value)} />
        </div>
        <div>
          <label>Mapped MHM Location</label>
          <Select defaultActiveFirstOption={false}
                  placeholder="- Not Mapped. Choose a location. -" value={locationName}
                  onChange={(value) => setLocationName(value)}
                  open={openSelectId === 1}
                  onDropdownVisibleChange={(open) =>
                    setOpenSelectId(open ? 1 : null)
                  }
                  dropdownRender={menu => (
                    <>
                      <Button type="text" icon={<PlusOutlined />}
                              onClick={onCreateNewLocationClick}>Create new location</Button>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  options={unmappedLocations.map(item => mapAsSelectOption(item)!)}
          />
        </div>
      </section>
      <footer>
        <Button size={"large"} onClick={closeModal}>Cancel</Button>
        <Button size={"large"} type={"primary"} onClick={onSave} loading={loading}>Save</Button>
      </footer>
    </Modal>
  </div>;
}
